import { canonicalPickerApi } from 'api/canonicalPicker'
import type { UidGroupRow } from 'api/canonicalPicker.types'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import { failToScopeCanonicalPicks, scopeCanonicalPicks } from '.'
import type { PickerDispatch } from './describeUnlabeledPicks'
import type { CanonicalLevel } from './types'

/**
 * Reads picked versions' groups by key from the specific source, for every
 * canonical product each belongs to, so level 2 offers the children of each
 * whichever way the version was picked. Versions it can't read are kept so
 * the merchant can ask again.
 */
const scopeUidGroupPicks =
  ({
    pickerId,
    level,
    source,
    versions,
  }: {
    pickerId: number
    level: CanonicalLevel
    /** The specific source the versions were picked from */
    source: PickerSource
    versions: UidGroupRow[]
  }) =>
  async (dispatch: PickerDispatch) => {
    if (versions.length === 0) return

    const request = dispatch(
      canonicalPickerApi.endpoints.getPickerRowsByKey.initiate({
        source,
        kind: 'uidGroup',
        keys: Array.from(new Set(versions.map(version => version.uidGroup))),
        scope: null,
      })
    )
    const { data } = await request
    request.unsubscribe()
    if (!data) {
      dispatch(
        failToScopeCanonicalPicks({
          pickerId,
          level,
          source: source.name,
          ids: versions.map(version => version.id),
        })
      )
      return
    }

    const found = new Map(
      data.rows.flatMap(row =>
        row.kind === 'uidGroup' ? [[row.id, row.productIds ?? []]] : []
      )
    )
    // A group the source doesn't list reaches only the product it names
    const productIds = Object.fromEntries(
      versions.map(version => [version.id, found.get(version.id) ?? []])
    )
    dispatch(
      scopeCanonicalPicks({ pickerId, level, source: source.name, productIds })
    )
  }

export default scopeUidGroupPicks
